import React from 'react';
import { useGame } from '../state/GameContext';
import { createRoomApi, joinRoomApi, uploadPhotoApi, startGameApi, submitGuessApi, resetGameApi } from '../api/gameApi';
import { sounds, haptics } from '../utils/sounds';

export function useGameActions() {
  const { state, dispatch } = useGame();

  const createRoom = async () => {
    if (!state.playerName.trim()) {
      dispatch({ type: 'SHOW_ERROR', message: 'Please enter your name' });
      sounds.error();
      haptics.error();
      return;
    }

    sounds.click();
    haptics.light();
    dispatch({ type: 'SET_FIELD', field: 'loading', value: true });
    dispatch({ type: 'CLEAR_MESSAGES' });

    try {
      const { roomCode: newRoomCode } = await createRoomApi();
      const joinData = await joinRoomApi(newRoomCode, state.playerName.trim());

      const finalPlayerName = joinData.sanitizedName || state.playerName.trim();

      dispatch({
        type: 'JOIN_SUCCESS',
        playerId: joinData.playerId,
        roomCode: newRoomCode,
        authToken: joinData.token,
        isHost: joinData.isHost || true,
        playerName: finalPlayerName,
      });

      sounds.success();
      haptics.success();
      window.history.pushState({}, '', `/${newRoomCode}`);

      localStorage.setItem(
        'photoBlenderSession',
        JSON.stringify({
          roomCode: newRoomCode,
          playerId: joinData.playerId,
          playerName: finalPlayerName,
          authToken: joinData.token,
          isHost: true,
        }),
      );
    } catch (err: any) {
      dispatch({ type: 'SHOW_ERROR', message: err.response?.data?.error || 'Failed to create room' });
    } finally {
      dispatch({ type: 'SET_FIELD', field: 'loading', value: false });
    }
  };

  const joinRoom = async () => {
    if (!state.playerName.trim() || !state.roomCode.trim()) {
      dispatch({ type: 'SHOW_ERROR', message: 'Please enter your name and room code' });
      sounds.error();
      haptics.error();
      return;
    }

    sounds.click();
    haptics.light();
    dispatch({ type: 'SET_FIELD', field: 'loading', value: true });
    dispatch({ type: 'CLEAR_MESSAGES' });

    try {
      const data = await joinRoomApi(state.roomCode.trim(), state.playerName.trim());

      const finalPlayerName = data.sanitizedName || state.playerName.trim();

      dispatch({
        type: 'JOIN_SUCCESS',
        playerId: data.playerId,
        roomCode: data.roomCode,
        authToken: data.token,
        isHost: data.isHost || false,
        playerName: finalPlayerName,
      });

      sounds.success();
      haptics.success();
      window.history.pushState({}, '', `/${data.roomCode}`);

      localStorage.setItem(
        'photoBlenderSession',
        JSON.stringify({
          roomCode: data.roomCode,
          playerId: data.playerId,
          playerName: finalPlayerName,
          authToken: data.token,
          isHost: data.isHost || false,
        }),
      );
    } catch (err: any) {
      dispatch({ type: 'SHOW_ERROR', message: err.response?.data?.error || 'Failed to join room' });
      sounds.error();
      haptics.error();
    } finally {
      dispatch({ type: 'SET_FIELD', field: 'loading', value: false });
    }
  };

  const uploadPhoto = async (file: File, skipLoadingState = false, photoIndex = 0, totalPhotos = 1) => {
    if (!file) return;

    if (!skipLoadingState) {
      dispatch({ type: 'SET_FIELD', field: 'loading', value: true });
      dispatch({ type: 'CLEAR_MESSAGES' });
    }

    try {
      await uploadPhotoApi(file, state.roomCode, state.playerId, (progress) => {
        dispatch({ type: 'SET_FIELD', field: 'uploadProgress', value: progress });
      }, photoIndex, totalPhotos);

      dispatch({ type: 'ADD_UPLOADED_PHOTO', url: URL.createObjectURL(file) });

      if (!skipLoadingState) {
        dispatch({ type: 'SHOW_SUCCESS', message: 'Photo uploaded successfully!' });
        sounds.notification();
        haptics.light();
        setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 3000);
      }
    } catch (err: any) {
      if (!skipLoadingState) {
        dispatch({ type: 'SHOW_ERROR', message: err.response?.data?.error || 'Failed to upload photo' });
      }
      throw err;
    } finally {
      if (!skipLoadingState) {
        dispatch({ type: 'SET_FIELD', field: 'loading', value: false });
      }
    }
  };

  const handleMultipleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    let filesToUpload: File[];

    if (state.uploadMode === 'auto') {
      const filesArray = Array.from(files);
      const shuffled = filesArray.sort(() => 0.5 - Math.random());
      filesToUpload = shuffled;
    } else {
      filesToUpload = Array.from(files);
    }

    // Reset input immediately so re-selecting the same files works
    event.target.value = '';

    dispatch({ type: 'SET_FIELD', field: 'loading', value: true });
    dispatch({ type: 'CLEAR_MESSAGES' });
    dispatch({ type: 'SET_FIELD', field: 'uploadProgress', value: 0 });

    let successCount = 0;
    let lastError = '';
    const totalFiles = filesToUpload.length;

    for (let i = 0; i < totalFiles; i++) {
      try {
        await uploadPhoto(filesToUpload[i], true, i, totalFiles);
        successCount++;
      } catch (err: any) {
        lastError = err.response?.data?.error || 'Upload failed';
        // If rate limited or at max photos, stop trying
        if (err.response?.status === 429 || err.response?.status === 400) {
          break;
        }
      }
    }

    dispatch({ type: 'SET_FIELD', field: 'loading', value: false });
    dispatch({ type: 'SET_FIELD', field: 'uploadProgress', value: 0 });

    const failedCount = totalFiles - successCount;
    if (successCount > 0 && failedCount === 0) {
      dispatch({
        type: 'SHOW_SUCCESS',
        message: `${successCount} photo${successCount > 1 ? 's' : ''} uploaded!`,
      });
      sounds.notification();
      haptics.light();
    } else if (successCount > 0 && failedCount > 0) {
      dispatch({
        type: 'SHOW_ERROR',
        message: `${successCount} uploaded, ${failedCount} failed: ${lastError}`,
      });
    } else {
      dispatch({ type: 'SHOW_ERROR', message: lastError || 'Failed to upload photos' });
      sounds.error();
      haptics.error();
    }

    setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 5000);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;

    if (files.length === 1) {
      uploadPhoto(files[0]);
    } else {
      // Reuse multi-upload logic
      dispatch({ type: 'SET_FIELD', field: 'loading', value: true });
      dispatch({ type: 'CLEAR_MESSAGES' });
      dispatch({ type: 'SET_FIELD', field: 'uploadProgress', value: 0 });

      let successCount = 0;
      let lastError = '';

      for (let i = 0; i < files.length; i++) {
        try {
          await uploadPhoto(files[i], true, i, files.length);
          successCount++;
        } catch (err: any) {
          lastError = err.response?.data?.error || 'Upload failed';
          if (err.response?.status === 429 || err.response?.status === 400) break;
        }
      }

      dispatch({ type: 'SET_FIELD', field: 'loading', value: false });
      dispatch({ type: 'SET_FIELD', field: 'uploadProgress', value: 0 });

      const failedCount = files.length - successCount;
      if (successCount > 0 && failedCount === 0) {
        dispatch({ type: 'SHOW_SUCCESS', message: `${successCount} photo${successCount > 1 ? 's' : ''} uploaded!` });
        sounds.notification();
        haptics.light();
      } else if (successCount > 0) {
        dispatch({ type: 'SHOW_ERROR', message: `${successCount} uploaded, ${failedCount} failed: ${lastError}` });
      } else {
        dispatch({ type: 'SHOW_ERROR', message: lastError || 'Failed to upload photos' });
        sounds.error();
        haptics.error();
      }

      setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 5000);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const startGame = async () => {
    sounds.click();
    haptics.light();
    dispatch({ type: 'SET_FIELD', field: 'loading', value: true });
    dispatch({ type: 'CLEAR_MESSAGES' });

    try {
      await startGameApi(state.roomCode, state.playerId);
      sounds.success();
      haptics.success();
    } catch (err: any) {
      dispatch({ type: 'SHOW_ERROR', message: err.response?.data?.error || 'Failed to start game' });
      sounds.error();
      haptics.error();
    } finally {
      dispatch({ type: 'SET_FIELD', field: 'loading', value: false });
    }
  };

  const submitGuess = async (guessedPlayerId?: string) => {
    const playerToGuess = guessedPlayerId || state.selectedPlayer;

    if (!playerToGuess) {
      dispatch({ type: 'SHOW_ERROR', message: 'Please select a player' });
      sounds.error();
      haptics.error();
      return;
    }

    if (state.hasSubmittedGuess) return;

    sounds.click();
    haptics.light();

    try {
      const timeToAnswer = 30 - state.timeRemaining;
      await submitGuessApi(state.roomCode, state.playerId, playerToGuess, timeToAnswer);

      dispatch({ type: 'SET_FIELD', field: 'selectedPlayer', value: playerToGuess });
      dispatch({ type: 'SET_FIELD', field: 'hasSubmittedGuess', value: true });
      dispatch({ type: 'SHOW_SUCCESS', message: 'Guess submitted! Waiting for other players...' });
      sounds.notification();
      haptics.success();
      setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 2000);
    } catch (err: any) {
      dispatch({ type: 'SHOW_ERROR', message: err.response?.data?.error || 'Failed to submit guess' });
      sounds.error();
      haptics.error();
    }
  };

  const resetGame = () => {
    sounds.click();
    haptics.light();

    localStorage.removeItem('photoBlenderSession');
    window.history.pushState({}, '', '/');

    if (state.socket) {
      state.socket.disconnect();
    }

    dispatch({ type: 'RESET_TO_HOME' });
  };

  const playAgain = async () => {
    sounds.click();
    haptics.light();

    try {
      await resetGameApi(state.roomCode, state.playerId);

      dispatch({ type: 'SET_FIELD', field: 'currentView', value: 'waiting' });
      dispatch({ type: 'SET_FIELD', field: 'currentPhoto', value: null });
      dispatch({ type: 'SET_FIELD', field: 'selectedPlayer', value: '' });
      dispatch({ type: 'SET_FIELD', field: 'photoResults', value: null });
      dispatch({ type: 'SET_FIELD', field: 'finalLeaderboard', value: null });
      dispatch({ type: 'SET_FIELD', field: 'uploadedPhotos', value: [] });
      dispatch({
        type: 'SET_FIELD',
        field: 'gameState',
        value: { ...state.gameState, gameState: 'waiting' },
      });
      dispatch({ type: 'SHOW_SUCCESS', message: 'Game reset! Upload photos to start a new game.' });
      sounds.success();
      haptics.success();
      setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 3000);
    } catch (err: any) {
      dispatch({ type: 'SHOW_ERROR', message: err.response?.data?.error || 'Failed to reset game' });
      sounds.error();
      haptics.error();
    }
  };

  const rejoinSession = () => {
    const savedSession = localStorage.getItem('photoBlenderSession');
    if (!savedSession) return;

    try {
      const session = JSON.parse(savedSession);
      dispatch({
        type: 'JOIN_SUCCESS',
        playerId: session.playerId,
        roomCode: session.roomCode,
        authToken: session.authToken,
        isHost: session.isHost,
        playerName: session.playerName,
      });
      dispatch({ type: 'SHOW_SUCCESS', message: 'Reconnecting to room...' });
    } catch {
      dispatch({ type: 'SHOW_ERROR', message: 'Failed to reconnect' });
      localStorage.removeItem('photoBlenderSession');
    }
  };

  return {
    createRoom,
    joinRoom,
    uploadPhoto,
    handleMultipleFileSelect,
    handleDrop,
    handleDragOver,
    startGame,
    submitGuess,
    resetGame,
    playAgain,
    rejoinSession,
  };
}
